<template>
    <div class="dashboard-body-futuristic">
        <main class="futuristic-container">
            <AppSidebar activeItem="standards" />

            <section class="dashboard-card-futuristic dashboard-card-scrollable">
                <div class="std-wrapper">

                    <!-- ===================== HEADER ===================== -->
                    <div class="std-header">
                        <div class="std-header-left">
                            <div class="std-header-icon">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                    stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                                    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                                </svg>
                            </div>
                            <div>
                                <h1 class="std-title">Standard Documents</h1>
                                <p class="std-subtitle">
                                    Search across every standard, code and specification in the shared library.
                                </p>
                            </div>
                        </div>
                        <button v-if="isSuperAdmin" class="std-btn primary" @click="openUpload">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="17 8 12 3 7 8" />
                                <line x1="12" y1="3" x2="12" y2="15" />
                            </svg>
                            Upload Documents
                        </button>
                    </div>

                    <!-- ===================== STATS ===================== -->
                    <div class="std-stats">
                        <div class="std-stat">
                            <span class="ss-num">{{ stats.total_documents || 0 }}</span>
                            <span class="ss-lbl">Documents</span>
                        </div>
                        <div class="std-stat">
                            <span class="ss-num">{{ formatSize(stats.total_size_bytes) }}</span>
                            <span class="ss-lbl">Total size</span>
                        </div>
                        <div class="std-stat">
                            <span class="ss-num">{{ formatNum(stats.total_chunks) }}</span>
                            <span class="ss-lbl">Indexed passages</span>
                        </div>
                        <div class="std-stat">
                            <span class="ss-num">{{ stats.categories || 0 }}</span>
                            <span class="ss-lbl">Categories</span>
                        </div>
                        <div v-if="stats.processing" class="std-stat processing">
                            <span class="ss-num">{{ stats.processing }}</span>
                            <span class="ss-lbl">Indexing…</span>
                        </div>
                        <div v-if="stats.failed" class="std-stat failed">
                            <span class="ss-num">{{ stats.failed }}</span>
                            <span class="ss-lbl">Failed</span>
                        </div>
                    </div>

                    <!-- ===================== SEARCH BAR ===================== -->
                    <div class="std-searchbar">
                        <div class="std-search-input">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                <circle cx="11" cy="11" r="8" />
                                <line x1="21" y1="21" x2="16.65" y2="16.65" />
                            </svg>
                            <input v-model="searchQuery" type="text" placeholder="Ask anything — e.g. “minimum grade of concrete for bridge deck”"
                                @keyup.enter="runSearch" />
                            <button v-if="searchQuery" class="clear-x" @click="clearSearch" title="Clear" aria-label="Clear search">&times;</button>
                        </div>
                        <select v-model="searchCategory" class="std-select" @change="onCategoryChange">
                            <option value="">All categories</option>
                            <option v-for="c in categories" :key="c.category" :value="c.category">
                                {{ c.category }} ({{ c.doc_count }})
                            </option>
                        </select>
                        <!-- Tables are indexed as their own chunks (row + column
                             headers), so they can be searched on their own. -->
                        <select v-model="contentType" class="std-select" @change="onCategoryChange"
                            title="Search prose, table data, or both">
                            <option value="">Text &amp; tables</option>
                            <option value="table">Tables only</option>
                            <option value="text">Text only</option>
                        </select>
                        <button class="std-btn primary" :disabled="searching || searchQuery.trim().length < 2"
                            @click="runSearch">
                            {{ searching ? 'Searching…' : 'Search' }}
                        </button>
                    </div>

                    <!-- ===================== SEARCH RESULTS ===================== -->
                    <div v-if="hasSearched" class="std-panel">
                        <div class="panel-head">
                            <h2>
                                Search results
                                <span class="res-count" v-if="!searching">{{ results.length }} document(s)</span>
                            </h2>
                            <button class="std-btn ghost" @click="clearSearch">Back to library</button>
                        </div>

                        <div v-if="searching" class="std-state"><div class="std-spinner"></div>Searching the library…</div>
                        <div v-else-if="!results.length" class="std-state empty">
                            No matches for “{{ lastQuery }}”. Try different wording or another category.
                        </div>

                        <div v-else class="result-list">
                            <div v-for="r in results" :key="r.doc_id" class="result-card">
                                <div class="result-head">
                                    <div class="rh-left">
                                        <span class="file-badge">{{ extOf(r.file_name) }}</span>
                                        <div>
                                            <span class="result-title">{{ r.title || r.file_name }}</span>
                                            <span class="result-meta">
                                                {{ r.category }} · {{ r.matches.length }} matching passage(s)
                                            </span>
                                        </div>
                                    </div>
                                    <div class="rh-right">
                                        <span class="score-pill" :title="'Relevance ' + pct(r.best_score)">
                                            {{ pct(r.best_score) }}
                                        </span>
                                        <button class="icon-btn" title="Download" aria-label="Download" @click="download(r.doc_id)">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                                stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                                stroke-linejoin="round">
                                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                                <polyline points="7 10 12 15 17 10" />
                                                <line x1="12" y1="15" x2="12" y2="3" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                                <div class="snippets">
                                    <div v-for="(m, i) in r.matches" :key="i" class="snippet"
                                        :class="{ 'is-table': m.content_type === 'table' }">
                                        <button class="snippet-page" type="button"
                                            :disabled="openingDocId === r.doc_id"
                                            :title="`Open ${r.file_name} at page ${(m.page || 0) + 1}`"
                                            @click="openAtPage(r.doc_id, m.page)">
                                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
                                                stroke="currentColor" stroke-width="2.5" stroke-linecap="round"
                                                stroke-linejoin="round">
                                                <path d="M14 3h7v7M21 3l-9 9M10 5H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-5" />
                                            </svg>
                                            p.{{ (m.page || 0) + 1 }}
                                        </button>
                                        <div class="snippet-body">
                                            <span v-if="m.content_type === 'table'" class="table-tag">
                                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
                                                    stroke="currentColor" stroke-width="2.5">
                                                    <rect x="3" y="3" width="18" height="18" rx="2" />
                                                    <path d="M3 9h18M3 15h18M9 3v18" />
                                                </svg>
                                                Table
                                            </span>
                                            <!-- Table chunks are one row per line; render them as
                                                 lines so the row structure stays readable. -->
                                            <template v-if="m.content_type === 'table'">
                                                <p v-for="(row, ri) in tableRows(m.content)" :key="ri"
                                                    class="snippet-text table-row">{{ row }}</p>
                                            </template>
                                            <p v-else class="snippet-text">{{ trim(m.content) }}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- ===================== LIBRARY BROWSER ===================== -->
                    <div v-else class="std-panel">
                        <div class="panel-head">
                            <h2>Library</h2>
                            <div class="browse-filter">
                                <input v-model="browseSearch" class="std-input-sm" type="text"
                                    placeholder="Filter by file name…" @input="onBrowseFilter" />
                            </div>
                        </div>

                        <!-- Category folders -->
                        <div v-if="categories.length" class="folder-grid">
                            <div class="folder-card" :class="{ active: searchCategory === '' }"
                                @click="selectCategory('')">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                    stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                    <path d="M3 7h18v13H3zM3 7l2-3h6l2 3" />
                                </svg>
                                <div>
                                    <span class="fc-name">All documents</span>
                                    <span class="fc-meta">{{ stats.total_documents || 0 }} files</span>
                                </div>
                            </div>
                            <div v-for="c in categories" :key="c.category" class="folder-card"
                                :class="{ active: searchCategory === c.category }" @click="selectCategory(c.category)">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                    stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                    <path d="M3 7h18v13H3zM3 7l2-3h6l2 3" />
                                </svg>
                                <div>
                                    <span class="fc-name">{{ c.category }}</span>
                                    <span class="fc-meta">{{ c.doc_count }} files · {{ formatSize(c.total_size) }}</span>
                                </div>
                            </div>
                        </div>

                        <!-- File table -->
                        <div v-if="loading" class="std-state"><div class="std-spinner"></div>Loading library…</div>
                        <div v-else-if="!documents.length" class="std-state empty">
                            <template v-if="isSuperAdmin">
                                No documents yet. Click “Upload Documents” to add the first standards.
                            </template>
                            <template v-else>
                                No documents in the library yet.
                            </template>
                        </div>

                        <div v-else class="std-table-wrap">
                            <table class="std-table">
                                <colgroup>
                                    <col>
                                    <col style="width: 150px;">
                                    <col style="width: 90px;">
                                    <col style="width: 140px;">
                                    <col style="width: 110px;">
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th>Document</th>
                                        <th>Category</th>
                                        <th class="ta-right">Size</th>
                                        <th>Status</th>
                                        <th class="ta-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="d in documents" :key="d.id">
                                        <td>
                                            <div class="doc-cell">
                                                <span class="file-badge">{{ extOf(d.file_name) }}</span>
                                                <div>
                                                    <span class="doc-name" :title="d.file_name">{{ d.title || d.file_name }}</span>
                                                    <span class="doc-sub" v-if="d.page_count">{{ d.page_count }} pages · {{ formatNum(d.chunk_count) }} passages</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td><span class="cat-pill">{{ d.category }}</span></td>
                                        <td class="nowrap ta-right">{{ formatSize(d.file_size) }}</td>
                                        <td>
                                            <span class="status-pill" :class="d.status.toLowerCase()">
                                                <span v-if="d.status === 'PROCESSING' || d.status === 'PENDING'" class="dot-pulse"></span>
                                                {{ statusLabel(d.status) }}
                                            </span>
                                            <span v-if="d.status === 'FAILED' && d.error_message" class="err-msg"
                                                :title="d.error_message">{{ trim(d.error_message, 60) }}</span>
                                        </td>
                                        <td class="ta-right nowrap">
                                            <button class="icon-btn" title="Download" aria-label="Download" @click="download(d.id)">
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                                    stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                                    stroke-linejoin="round">
                                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                                    <polyline points="7 10 12 15 17 10" />
                                                    <line x1="12" y1="15" x2="12" y2="3" />
                                                </svg>
                                            </button>
                                            <button v-if="isSuperAdmin && d.status === 'FAILED'" class="icon-btn"
                                                title="Retry indexing" aria-label="Retry indexing" @click="reindex(d.id)">
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                                    stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                                    stroke-linejoin="round">
                                                    <polyline points="23 4 23 10 17 10" />
                                                    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                                                </svg>
                                            </button>
                                            <button v-if="isSuperAdmin" class="icon-btn danger" title="Delete" aria-label="Delete"
                                                @click="confirmDelete(d)">
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                                    stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                                    stroke-linejoin="round">
                                                    <polyline points="3 6 5 6 21 6" />
                                                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                                                    <path d="M10 11v6M14 11v6" />
                                                </svg>
                                            </button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <AppPagination v-if="totalPages > 1" :currentPage="page" :totalPages="totalPages"
                            @page-changed="fetchDocuments" />
                    </div>
                </div>
            </section>
        </main>

        <!-- ===================== UPLOAD MODAL (Super Admin) ===================== -->
        <div v-if="uploadModal" class="std-modal-backdrop" @click.self="closeUpload">
            <div class="std-modal">
                <div class="modal-head">
                    <h3>Upload Standard Documents</h3>
                    <button class="close-x" @click="closeUpload" aria-label="Close">&times;</button>
                </div>

                <div class="modal-body">
                    <label class="fld">
                        <span class="fld-label">Category folder</span>
                        <input v-model="uploadCategory" list="std-cat-list" type="text"
                            placeholder="e.g. IRC or IRC/Bridges" />
                        <datalist id="std-cat-list">
                            <option v-for="c in categories" :key="c.category" :value="c.category" />
                        </datalist>
                        <span class="fld-hint">Files are stored in this folder. Use “/” for sub-folders.</span>
                    </label>

                    <label class="fld">
                        <span class="fld-label">Description <em>(optional)</em></span>
                        <textarea v-model="uploadDescription" rows="2"
                            placeholder="Short note about this batch of documents"></textarea>
                    </label>

                    <label class="fld">
                        <span class="fld-label">Files</span>
                        <div class="dropzone" :class="{ dragging: isDragging }" @dragover.prevent="isDragging = true"
                            @dragleave.prevent="isDragging = false" @drop.prevent="onDrop">
                            <input ref="fileInput" type="file" multiple class="file-hidden"
                                @change="onFilePick" />
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="17 8 12 3 7 8" />
                                <line x1="12" y1="3" x2="12" y2="15" />
                            </svg>
                            <p>Drag &amp; drop files here, or
                                <button type="button" class="link-btn" @click="$refs.fileInput.click()">browse</button>
                            </p>
                            <span class="dz-hint">PDF, DOCX, TXT, MD, CSV are indexed for search. Large batches are fine.</span>
                        </div>
                    </label>

                    <div v-if="selectedFiles.length" class="picked-list">
                        <div class="picked-head">
                            <span>{{ selectedFiles.length }} file(s) · {{ formatSize(totalPickedSize) }}</span>
                            <button class="link-btn" @click="selectedFiles = []">Clear all</button>
                        </div>
                        <div v-for="(f, i) in selectedFiles" :key="i" class="picked-row">
                            <span class="file-badge sm">{{ extOf(f.name) }}</span>
                            <span class="picked-name" :title="f.name">{{ f.name }}</span>
                            <span class="picked-size">{{ formatSize(f.size) }}</span>
                            <button class="close-x sm" @click="selectedFiles.splice(i, 1)" :aria-label="`Remove ${f.name}`">&times;</button>
                        </div>
                    </div>

                    <div v-if="uploading" class="upload-progress">
                        <div class="up-bar"><div class="up-fill" :style="{ width: uploadPercent + '%' }"></div></div>
                        <span class="up-text">Uploading… {{ uploadPercent }}%</span>
                    </div>

                    <p v-if="uploadError" class="form-error">{{ uploadError }}</p>
                </div>

                <div class="modal-foot">
                    <button class="std-btn ghost" @click="closeUpload" :disabled="uploading">Cancel</button>
                    <button class="std-btn primary" :disabled="!selectedFiles.length || uploading"
                        @click="submitUpload">
                        {{ uploading ? 'Uploading…' : `Upload ${selectedFiles.length || ''} file(s)` }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import AppSidebar from './Sidebar.vue';
import AppPagination from './AppPagination.vue';
import { getUserInfo } from '../utils/auth';
import { notify } from '@/utils/notify';
import { confirmAction } from '@/utils/confirm';

export default {
    name: 'StandardDocuments',
    components: { AppSidebar, AppPagination },
    props: { getAccessToken: Function },
    data() {
        return {
            // library browse
            documents: [],
            categories: [],
            stats: {},
            page: 1,
            totalPages: 1,
            loading: false,
            browseSearch: '',
            browseTimeout: null,

            // search
            searchQuery: '',
            searchCategory: '',
            // '' = prose + tables, 'table' = tabular data only, 'text' = prose only
            contentType: '',
            results: [],
            searching: false,
            hasSearched: false,
            lastQuery: '',
            // doc_id currently being opened in the PDF viewer (disables its links)
            openingDocId: null,

            // upload (super admin)
            uploadModal: false,
            uploadCategory: '',
            uploadDescription: '',
            selectedFiles: [],
            uploading: false,
            uploadPercent: 0,
            uploadError: '',
            isDragging: false,

            pollTimer: null,
        };
    },
    computed: {
        // Upload/delete are Super Admin only — mirrors the backend guard.
        isSuperAdmin() {
            const info = getUserInfo();
            return !!(info && info.is_super_admin_company === true);
        },
        totalPickedSize() {
            return this.selectedFiles.reduce((sum, f) => sum + (f.size || 0), 0);
        },
    },
    mounted() {
        this.fetchAll();
    },
    beforeUnmount() {
        clearTimeout(this.browseTimeout);
        clearInterval(this.pollTimer);
    },
    methods: {
        authHeader() {
            const token = this.getAccessToken ? this.getAccessToken() : null;
            return { headers: { Authorization: 'Bearer ' + token } };
        },
        fetchAll() {
            this.fetchStats();
            this.fetchCategories();
            this.fetchDocuments(1);
        },
        async fetchStats() {
            try {
                const res = await axios.get(this.$API_URL + '/standard-documents/stats', this.authHeader());
                this.stats = res.data || {};
                // While anything is still indexing, refresh periodically so the
                // status pills flip to "Indexed" without a manual reload.
                if (this.stats.processing > 0) this.startPolling();
                else this.stopPolling();
            } catch (e) { console.error(e); }
        },
        async fetchCategories() {
            try {
                const res = await axios.get(this.$API_URL + '/standard-documents/categories', this.authHeader());
                this.categories = res.data.items || [];
            } catch (e) { console.error(e); }
        },
        async fetchDocuments(page = 1) {
            this.loading = true;
            this.page = page;
            try {
                let url = `${this.$API_URL}/standard-documents/list?page=${page}&page_size=20`;
                if (this.searchCategory) url += `&category=${encodeURIComponent(this.searchCategory)}`;
                const q = this.browseSearch.trim();
                if (q) url += `&search=${encodeURIComponent(q)}`;
                const res = await axios.get(url, this.authHeader());
                this.documents = res.data.items || [];
                this.totalPages = res.data.total_pages || 1;
            } catch (e) {
                console.error(e);
                this.documents = [];
            } finally {
                this.loading = false;
            }
        },
        onBrowseFilter() {
            clearTimeout(this.browseTimeout);
            this.browseTimeout = setTimeout(() => this.fetchDocuments(1), 300);
        },
        selectCategory(cat) {
            this.searchCategory = cat;
            this.fetchDocuments(1);
        },
        onCategoryChange() {
            // Re-run whichever view is active so the filter applies to both.
            if (this.hasSearched) this.runSearch();
            else this.fetchDocuments(1);
        },

        // ---- semantic search (all users) ----
        async runSearch() {
            const q = this.searchQuery.trim();
            if (q.length < 2) return;
            this.searching = true;
            this.hasSearched = true;
            this.lastQuery = q;
            try {
                let url = `${this.$API_URL}/standard-documents/search?q=${encodeURIComponent(q)}&limit=20&grouped=true`;
                if (this.searchCategory) url += `&category=${encodeURIComponent(this.searchCategory)}`;
                if (this.contentType) url += `&content_type=${this.contentType}`;
                const res = await axios.get(url, this.authHeader());
                this.results = res.data.results || [];
            } catch (e) {
                console.error(e);
                this.results = [];
            } finally {
                this.searching = false;
            }
        },
        clearSearch() {
            this.searchQuery = '';
            this.results = [];
            this.hasSearched = false;
            this.lastQuery = '';
        },

        // ---- download (all users) ----
        async download(docId) {
            try {
                const res = await axios.get(
                    `${this.$API_URL}/standard-documents/${docId}/download`,
                    { ...this.authHeader(), responseType: 'blob' }
                );
                const disp = res.headers['content-disposition'] || '';
                const match = disp.match(/filename=([^;]+)/);
                const name = match ? match[1].trim().replace(/"/g, '') : `document-${docId}`;
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const a = document.createElement('a');
                a.href = url;
                a.download = name;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            } catch (e) {
                notify.error(`Couldn’t download the document: ${e.response?.data?.detail || e.message}. Try again.`);
            }
        },

        // ---- open a match's source PDF at its page (all users) ----
        // The view endpoint needs an auth header, so a plain link can't be used.
        // We fetch the file as a blob, then open it in a new tab with a
        // `#page=N` fragment — which the browser's built-in PDF viewer honours
        // and scrolls straight to that page. `page` from the API is 0-based, so
        // we add 1 for the human/PDF-viewer page number.
        async openAtPage(docId, page) {
            if (this.openingDocId) return;              // ignore double-clicks
            this.openingDocId = docId;
            // Open the tab synchronously (inside the click) so the browser
            // doesn't treat the later programmatic open() as a blocked popup.
            const tab = window.open('', '_blank');
            try {
                const res = await axios.get(
                    `${this.$API_URL}/standard-documents/${docId}/view`,
                    { ...this.authHeader(), responseType: 'blob' }
                );
                const blob = new Blob([res.data], { type: 'application/pdf' });
                const pageNo = (Number(page) || 0) + 1;
                const url = window.URL.createObjectURL(blob) + `#page=${pageNo}`;
                if (tab) {
                    tab.location = url;
                } else {
                    // Popup blocked — fall back to navigating the current tab.
                    window.location.href = url;
                }
                // Revoke after the viewer has had time to load the blob.
                setTimeout(() => window.URL.revokeObjectURL(url.split('#')[0]), 60000);
            } catch (e) {
                if (tab) tab.close();
                notify.error(`Couldn’t open the document: ${e.response?.data?.detail || e.message}. Try again.`);
            } finally {
                this.openingDocId = null;
            }
        },

        // ---- upload (super admin) ----
        openUpload() {
            this.uploadModal = true;
            this.uploadError = '';
            this.uploadPercent = 0;
            this.uploadCategory = this.searchCategory || '';
        },
        closeUpload() {
            if (this.uploading) return;
            this.uploadModal = false;
            this.selectedFiles = [];
            this.uploadDescription = '';
            this.uploadError = '';
        },
        onFilePick(e) {
            this.addFiles(Array.from(e.target.files || []));
            e.target.value = '';
        },
        onDrop(e) {
            this.isDragging = false;
            this.addFiles(Array.from(e.dataTransfer.files || []));
        },
        addFiles(files) {
            const existing = new Set(this.selectedFiles.map(f => f.name + f.size));
            files.forEach(f => {
                if (!existing.has(f.name + f.size)) this.selectedFiles.push(f);
            });
        },
        async submitUpload() {
            if (!this.selectedFiles.length) return;
            this.uploading = true;
            this.uploadError = '';
            this.uploadPercent = 0;

            const form = new FormData();
            this.selectedFiles.forEach(f => form.append('uploaded_files', f));
            form.append('category', (this.uploadCategory || 'Uncategorized').trim());
            if (this.uploadDescription.trim()) form.append('description', this.uploadDescription.trim());

            try {
                await axios.post(this.$API_URL + '/standard-documents/upload', form, {
                    ...this.authHeader(),
                    // Large libraries: no client timeout, and report real progress.
                    timeout: 0,
                    onUploadProgress: (evt) => {
                        if (evt.total) {
                            this.uploadPercent = Math.round((evt.loaded * 100) / evt.total);
                        }
                    },
                });
                this.uploading = false;
                this.uploadModal = false;
                this.selectedFiles = [];
                this.uploadDescription = '';
                // Indexing continues server-side; refresh and poll until done.
                this.fetchAll();
                this.startPolling();
            } catch (e) {
                this.uploading = false;
                this.uploadError = e.response?.data?.detail || e.message || 'Upload failed.';
            }
        },

        async reindex(docId) {
            try {
                await axios.post(`${this.$API_URL}/standard-documents/${docId}/reindex`, {}, this.authHeader());
                this.fetchDocuments(this.page);
                this.startPolling();
            } catch (e) {
                notify.error(`Couldn’t restart indexing: ${e.response?.data?.detail || e.message}. Try again.`);
            }
        },
        async confirmDelete(doc) {
            const ok = await confirmAction({
                title: 'Delete Document?',
                message: `“${doc.file_name}” and its search index are removed from the library. This can’t be undone.`,
                confirmLabel: 'Delete Document',
                destructive: true,
            });
            if (!ok) return;
            try {
                await axios.delete(`${this.$API_URL}/standard-documents/${doc.id}`, this.authHeader());
                this.fetchAll();
                notify.success('Document deleted');
            } catch (e) {
                notify.error(`Couldn’t delete the document: ${e.response?.data?.detail || e.message}. Try again.`);
            }
        },

        // Poll while documents are still being indexed in the background.
        startPolling() {
            if (this.pollTimer) return;
            this.pollTimer = setInterval(() => {
                this.fetchStats();
                if (!this.hasSearched) this.fetchDocuments(this.page);
            }, 5000);
        },
        stopPolling() {
            clearInterval(this.pollTimer);
            this.pollTimer = null;
        },

        // ---- formatting helpers ----
        formatSize(bytes) {
            const b = Number(bytes || 0);
            if (!b) return '0 B';
            const units = ['B', 'KB', 'MB', 'GB', 'TB'];
            const i = Math.min(units.length - 1, Math.floor(Math.log(b) / Math.log(1024)));
            return `${(b / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
        },
        formatNum(n) {
            return Number(n || 0).toLocaleString();
        },
        extOf(name) {
            const e = (name || '').split('.').pop();
            return (e && e.length <= 5 ? e : 'file').toUpperCase();
        },
        pct(score) {
            return Math.round(Math.max(0, Math.min(1, Number(score || 0))) * 100) + '%';
        },
        trim(text, len = 260) {
            const t = (text || '').replace(/\s+/g, ' ').trim();
            return t.length > len ? t.slice(0, len) + '…' : t;
        },
        // A table chunk is one serialized row per line ("Header: value | …").
        // Split it back into lines so the row structure survives into the UI.
        tableRows(content, max = 6) {
            const rows = (content || '').split('\n').map(r => r.trim()).filter(Boolean);
            return rows.slice(0, max).map(r => this.trim(r, 200));
        },
        statusLabel(s) {
            return { COMPLETED: 'Indexed', PROCESSING: 'Indexing', PENDING: 'Queued', FAILED: 'Failed' }[s] || s;
        },
    },
};
</script>

<style scoped>
.std-wrapper { padding: 4px 2px 30px; }

/* header */
.std-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; margin-bottom: 18px; flex-wrap: wrap; }
.std-header-left { display: flex; gap: 14px; align-items: flex-start; }
.std-header-icon { width: 46px; height: 46px; border-radius: 12px; display: grid; place-items: center; background: linear-gradient(135deg, #6366f1, #8b5cf6); color: #fff; flex: none; }
.std-title { margin: 0 0 3px; font-size: 1.35rem; font-weight: 800; color: #0f172a; letter-spacing: -0.02em; }
.std-subtitle { margin: 0; font-size: 0.86rem; color: #64748b; max-width: 62ch; }

/* stats */
.std-stats { display: grid; grid-template-columns: repeat(auto-fit, minmax(120px, 1fr)); gap: 10px; margin-bottom: 18px; }
.std-stat { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px 14px; display: flex; flex-direction: column; gap: 2px; }
.ss-num { font-size: 1.15rem; font-weight: 800; color: #0f172a; }
.ss-lbl { font-size: 0.72rem; color: #64748b; text-transform: uppercase; letter-spacing: 0.05em; }
.std-stat.processing { background: #fffbeb; border-color: #fde68a; }
.std-stat.processing .ss-num { color: #b45309; }
.std-stat.failed { background: #fef2f2; border-color: #fecaca; }
.std-stat.failed .ss-num { color: #b91c1c; }

/* search bar */
.std-searchbar { display: flex; gap: 10px; margin-bottom: 18px; flex-wrap: wrap; }
.std-search-input { position: relative; flex: 1 1 340px; display: flex; align-items: center; }
.std-search-input svg { position: absolute; left: 13px; color: #64748b; pointer-events: none; }
.std-search-input input { width: 100%; padding: 11px 34px 11px 40px; border: 1px solid #cbd5e1; border-radius: 10px; font-size: 0.9rem; outline: none; }
.std-search-input input:focus { border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99, 102, 241, .12); }
.clear-x { position: absolute; right: 10px; background: none; border: none; font-size: 1.15rem; color: #64748b; cursor: pointer; line-height: 1; }
.std-select, .std-input-sm { padding: 10px 12px; border: 1px solid #cbd5e1; border-radius: 10px; font-size: 0.86rem; background: #fff; outline: none; }
.std-select:focus, .std-input-sm:focus { border-color: #6366f1; }

/* buttons */
.std-btn { display: inline-flex; align-items: center; gap: 7px; padding: 10px 16px; border-radius: 10px; font-size: 0.85rem; font-weight: 600; cursor: pointer; border: 1px solid transparent; transition: filter .15s ease; }
.std-btn.primary { background: #0071C5; color: #fff; }
.std-btn.primary:hover:not(:disabled) { filter: brightness(1.07); }
.std-btn.primary:disabled { opacity: .55; cursor: not-allowed; }
.std-btn.ghost { background: #fff; border-color: #cbd5e1; color: #475569; }
.std-btn.ghost:hover { border-color: #94a3b8; }
.std-btn.danger { background: #b42318; color: #fff; }
.icon-btn { background: none; border: 1px solid #e2e8f0; border-radius: 8px; padding: 6px; color: #64748b; cursor: pointer; margin-left: 5px; }
.icon-btn:hover { color: #6366f1; border-color: #c7d2fe; background: #eef2ff; }
.icon-btn.danger:hover { color: #dc2626; border-color: #fecaca; background: #fef2f2; }
.link-btn { background: none; border: none; color: #6366f1; font-weight: 600; cursor: pointer; padding: 0; font-size: inherit; text-decoration: underline; }

/* panels */
.std-panel { background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 18px; }
.panel-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 14px; flex-wrap: wrap; }
.panel-head h2 { margin: 0; font-size: 1.02rem; font-weight: 700; color: #0f172a; display: flex; align-items: center; gap: 9px; }
.res-count { font-size: 0.76rem; font-weight: 600; color: #64748b; background: #f1f5f9; padding: 3px 9px; border-radius: 20px; }

/* states */
.std-state { display: flex; align-items: center; justify-content: center; gap: 10px; padding: 40px 16px; color: #64748b; font-size: 0.88rem; }
.std-state.empty { color: #64748b; }
.std-spinner { width: 18px; height: 18px; border: 2px solid #e2e8f0; border-top-color: #6366f1; border-radius: 50%; animation: spin .7s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

/* folder grid */
.folder-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(210px, 1fr)); gap: 10px; margin-bottom: 18px; }
.folder-card { display: flex; align-items: center; gap: 11px; padding: 12px 14px; border: 1px solid #e2e8f0; border-radius: 10px; cursor: pointer; background: #f8fafc; transition: all .15s ease; }
.folder-card:hover { border-color: #c7d2fe; background: #eef2ff; }
.folder-card.active { border-color: #6366f1; background: #eef2ff; box-shadow: 0 0 0 2px rgba(99, 102, 241, .12); }
.folder-card svg { color: #6366f1; flex: none; }
.fc-name { display: block; font-size: 0.86rem; font-weight: 650; color: #1e293b; }
.fc-meta { display: block; font-size: 0.73rem; color: #64748b; }

/* table */
.std-table-wrap { overflow-x: auto; }
.std-table { width: 100%; border-collapse: collapse; font-size: 0.85rem; table-layout: fixed; }
.std-table th { text-align: left; vertical-align: middle; padding: 0.75rem 12px; font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: #475569; background: #f1f5f9; border-bottom: 2px solid #e2e8f0; white-space: nowrap; }
.std-table td { text-align: left; padding: 11px 12px; border-bottom: 1px solid #f1f5f9; color: #334155; vertical-align: middle; }
.std-table tbody tr:nth-child(even) { background: #fafbfc; }
.std-table tbody tr:hover { background: #eff6ff; }
.ta-right { text-align: right; }
.nowrap { white-space: nowrap; }
.doc-cell { display: flex; align-items: center; gap: 10px; }
.doc-name { display: block; font-weight: 600; color: #1e293b; max-width: 340px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.doc-sub { display: block; font-size: 0.73rem; color: #64748b; }
.file-badge { flex: none; display: grid; place-items: center; min-width: 38px; height: 30px; padding: 0 7px; border-radius: 7px; background: #eef2ff; color: #4f46e5; font-size: 0.64rem; font-weight: 800; letter-spacing: 0.03em; }
.file-badge.sm { min-width: 32px; height: 24px; font-size: 0.6rem; }
.cat-pill { display: inline-block; padding: 3px 10px; border-radius: 20px; background: #f1f5f9; color: #475569; font-size: 0.75rem; font-weight: 600; }
.status-pill { display: inline-flex; align-items: center; gap: 6px; padding: 3px 10px; border-radius: 20px; font-size: 0.72rem; font-weight: 700; }
.status-pill.completed { background: #dcfce7; color: #15803d; }
.status-pill.processing, .status-pill.pending { background: #fef3c7; color: #b45309; }
.status-pill.failed { background: #fee2e2; color: #b91c1c; }
.dot-pulse { width: 6px; height: 6px; border-radius: 50%; background: currentColor; animation: pulse 1.2s ease-in-out infinite; }
@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: .3; } }
.err-msg { display: block; font-size: 0.7rem; color: #b91c1c; margin-top: 3px; max-width: 220px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

/* results */
.result-list { display: flex; flex-direction: column; gap: 12px; }
.result-card { border: 1px solid #e2e8f0; border-radius: 12px; padding: 14px 16px; background: #fff; }
.result-card:hover { border-color: #c7d2fe; }
.result-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; margin-bottom: 10px; }
.rh-left { display: flex; gap: 11px; align-items: center; }
.rh-right { display: flex; align-items: center; gap: 6px; flex: none; }
.result-title { display: block; font-weight: 700; color: #1e293b; font-size: 0.92rem; }
.result-meta { display: block; font-size: 0.75rem; color: #64748b; }
.score-pill { background: #eef2ff; color: #4f46e5; font-size: 0.72rem; font-weight: 700; padding: 4px 10px; border-radius: 20px; }
.snippets { display: flex; flex-direction: column; gap: 8px; }
.snippet { display: flex; gap: 10px; padding: 9px 11px; background: #f8fafc; border-left: 3px solid #c7d2fe; border-radius: 0 8px 8px 0; }
/* snippet-page is a button that opens the source PDF at this page */
.snippet-page {
    flex: none; display: inline-flex; align-items: center; gap: 3px;
    font-size: 0.7rem; font-weight: 700; color: #6366f1;
    background: #eef2ff; border: 1px solid #c7d2fe; border-radius: 6px;
    padding: 2px 7px; height: fit-content; cursor: pointer;
    transition: background .15s ease, border-color .15s ease; white-space: nowrap;
}
.snippet-page:hover { background: #6366f1; color: #fff; border-color: #6366f1; }
.snippet-page:disabled { opacity: .55; cursor: wait; }
.snippet-page svg { flex: none; }
.snippet-body { min-width: 0; flex: 1; }
.snippet-text { margin: 0; font-size: 0.82rem; color: #475569; line-height: 1.5; }
/* table hits: amber rail + monospace rows so columns stay scannable */
.snippet.is-table { background: #fffbeb; border-left-color: #fcd34d; }
.snippet.is-table .snippet-page { color: #b45309; background: #fef3c7; border-color: #fcd34d; }
.snippet.is-table .snippet-page:hover { background: #b45309; color: #fff; border-color: #b45309; }
.table-tag { display: inline-flex; align-items: center; gap: 4px; font-size: 0.64rem; font-weight: 800; letter-spacing: 0.04em; text-transform: uppercase; color: #b45309; background: #fef3c7; padding: 2px 7px; border-radius: 20px; margin-bottom: 5px; }
.snippet-text.table-row { font-family: ui-monospace, "Cascadia Mono", Menlo, Consolas, monospace; font-size: 0.75rem; color: #475569; padding: 1px 0; overflow-wrap: anywhere; }

/* modal */
.std-modal-backdrop { position: fixed; inset: 0; background: rgba(15, 23, 42, .55); display: grid; place-items: center; z-index: 1000; padding: 20px; }
.std-modal { background: #fff; border-radius: 16px; width: 100%; max-width: 560px; max-height: 88vh; display: flex; flex-direction: column; box-shadow: 0 20px 50px rgba(0, 0, 0, .25); }
.modal-head { display: flex; align-items: center; justify-content: space-between; padding: 16px 20px; border-bottom: 1px solid #e2e8f0; }
.modal-head h3 { margin: 0; font-size: 1.02rem; font-weight: 700; color: #0f172a; }
.close-x { background: none; border: none; font-size: 1.5rem; line-height: 1; color: #64748b; cursor: pointer; }
.close-x.sm { font-size: 1.1rem; }
.modal-body { padding: 18px 20px; overflow-y: auto; display: flex; flex-direction: column; gap: 14px; }
.modal-foot { display: flex; justify-content: flex-end; gap: 9px; padding: 14px 20px; border-top: 1px solid #e2e8f0; }
.fld { display: flex; flex-direction: column; gap: 5px; }
.fld-label { font-size: 0.8rem; font-weight: 650; color: #334155; }
.fld-label em { font-weight: 400; color: #64748b; font-style: normal; }
.fld input, .fld textarea { padding: 9px 12px; border: 1px solid #cbd5e1; border-radius: 8px; font-size: 0.86rem; outline: none; font-family: inherit; }
.fld input:focus, .fld textarea:focus { border-color: #6366f1; }
.fld-hint { font-size: 0.73rem; color: #64748b; }

/* dropzone */
.dropzone { border: 2px dashed #cbd5e1; border-radius: 12px; padding: 22px 16px; text-align: center; color: #64748b; background: #f8fafc; transition: all .15s ease; }
.dropzone.dragging { border-color: #6366f1; background: #eef2ff; }
.dropzone svg { color: #64748b; margin-bottom: 6px; }
.dropzone p { margin: 0 0 4px; font-size: 0.85rem; }
.dz-hint { font-size: 0.72rem; color: #64748b; }
.file-hidden { display: none; }

/* picked files */
.picked-list { border: 1px solid #e2e8f0; border-radius: 10px; max-height: 190px; overflow-y: auto; }
.picked-head { display: flex; justify-content: space-between; align-items: center; padding: 8px 11px; background: #f8fafc; font-size: 0.78rem; color: #475569; font-weight: 600; border-bottom: 1px solid #e2e8f0; position: sticky; top: 0; }
.picked-row { display: flex; align-items: center; gap: 9px; padding: 7px 11px; border-bottom: 1px solid #f1f5f9; }
.picked-row:last-child { border-bottom: none; }
.picked-name { flex: 1; font-size: 0.8rem; color: #334155; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.picked-size { font-size: 0.73rem; color: #64748b; flex: none; }

/* upload progress */
.upload-progress { display: flex; flex-direction: column; gap: 5px; }
.up-bar { height: 7px; background: #e2e8f0; border-radius: 20px; overflow: hidden; }
.up-fill { height: 100%; background: linear-gradient(90deg, #6366f1, #8b5cf6); transition: width .2s ease; }
.up-text { font-size: 0.75rem; color: #64748b; }
.form-error { margin: 0; color: #b91c1c; font-size: 0.82rem; background: #fef2f2; border: 1px solid #fecaca; padding: 8px 11px; border-radius: 8px; }

@media (max-width: 640px) {
    .std-header { flex-direction: column; align-items: stretch; }
    .doc-name { max-width: 170px; }
}
</style>
